/**
 * Onboarding's Firm Setup card (`show_firm_setup` output): the firm items
 * this member sees, which are done, and which they may set up. Locked items
 * name the owner or admin to ask; firm-wide decisions only reach owners and
 * admins (the server leaves them out for everyone else). A row's action says
 * what to do next as the member's message; a row whose item lives in a
 * Settings section also opens it there.
 */
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import { Building2, Lock, Upload } from "lucide-react";
import { useMemo, type FC } from "react";

import { Button } from "@/components/ui/button";
import { useOfferActionsContext } from "@/features/caseIde/chat/offers/OfferActionsContext";
import { cn } from "@/lib/utils";

import { firmItemCopy, withLiveItems } from "../firmItems";
import { useOnboarding } from "../hooks/useOnboarding";
import type { FirmSetupItem, FirmSetupOutput } from "../types";
import { OpenSettingsButton } from "./OpenSettingsButton";
import { StepDot } from "./StepDot";

type AnyToolPart = ToolUIPart | DynamicToolUIPart;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

export const firmSetupFromOutput = (
  output: unknown
): FirmSetupOutput | null => {
  if (!isRecord(output) || !Array.isArray(output.items)) return null;
  const items = output.items.filter(
    (i): i is FirmSetupItem =>
      isRecord(i) && typeof i.id === "string" && typeof i.label === "string"
  );
  return {
    firm_name: typeof output.firm_name === "string" ? output.firm_name : null,
    is_admin: Boolean(output.is_admin),
    items,
    done: items.filter((i) => i.done).length,
    total: items.length
  };
};

const ItemRow: FC<{
  item: FirmSetupItem;
  disabled: boolean;
  onAct?: (prompt: string) => void;
}> = ({ item, disabled, onAct }) => {
  const copy = firmItemCopy(item.id);
  return (
    <li
      className="flex items-center gap-3 px-2.5 py-2"
      data-testid={`firm-item-${item.id}`}
      data-done={item.done ? "" : undefined}
    >
      <StepDot state={item.done ? "done" : item.skipped ? "skipped" : "open"} />
      <div className="min-w-0 flex-1">
        <div className={cn("font-medium", item.done ? "text-fg-3" : "text-fg")}>
          {item.label}
        </div>
        {copy.description ? (
          <div className="text-sm text-fg-3">{copy.description}</div>
        ) : null}
      </div>
      {copy.settingsSection && item.can_edit ? (
        <OpenSettingsButton
          section={copy.settingsSection}
          testId={`firm-item-settings-${item.id}`}
        />
      ) : null}
      {item.done ? (
        <span className="text-sm text-fg-4">Done</span>
      ) : !item.can_edit ? (
        <span className="inline-flex items-center gap-1 text-sm text-fg-4">
          <Lock className="size-3" />
          Owner or Admin
        </span>
      ) : onAct ? (
        <Button
          variant="secondary"
          size="xs"
          disabled={disabled}
          onClick={() => onAct(copy.prompt)}
        >
          {item.id === "templates" ? <Upload /> : null}
          {copy.action}
        </Button>
      ) : null}
    </li>
  );
};

export const FirmSetupCard: FC<{ part: AnyToolPart; className?: string }> = ({
  part,
  className
}) => {
  const snapshot = useMemo(
    () =>
      part.state === "output-available"
        ? firmSetupFromOutput(part.output)
        : null,
    [part]
  );
  const { progress } = useOnboarding(Boolean(snapshot));
  const offers = useOfferActionsContext();
  if (!snapshot || !snapshot.items.length) return null;
  const items = withLiveItems(snapshot.items, progress?.firm_items);
  const done = items.filter((i) => i.done).length;
  const admin = snapshot.is_admin;
  const groups = admin
    ? (["info", "decision"] as const).map((kind) => ({
        kind,
        items: items.filter((i) => i.kind === kind)
      }))
    : [{ kind: "info" as const, items }];
  const locked = items.find((i) => !i.done && !i.can_edit);
  const say = offers?.say;

  return (
    <div
      className={cn(
        "rounded-xl bg-elevated p-3 text-base inset-ring inset-ring-line-2",
        className
      )}
      data-testid="firm-setup-card"
    >
      <div className="flex items-center gap-1.5 text-fg-3">
        <Building2 className="size-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          Set Up {snapshot.firm_name || "Your Firm"}
        </span>
        <span className="text-sm tabular-nums">
          {done} of {items.length} Done
        </span>
      </div>
      {groups.map(({ kind, items: group }) =>
        group.length ? (
          <div key={kind}>
            {admin ? (
              <div className="mt-2.5 px-0.5 pb-1.5 text-sm text-fg-3">
                {kind === "info" ? "Firm Information" : "Firm-Wide Decisions"}
              </div>
            ) : null}
            <ul
              className={cn(
                "divide-y divide-line-1 rounded-md bg-fill-1",
                !admin && "mt-2.5"
              )}
            >
              {group.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  disabled={offers?.busy ?? true}
                  onAct={say}
                />
              ))}
            </ul>
          </div>
        ) : null
      )}
      {!admin && locked ? (
        <p className="mx-0.5 mt-2 text-sm text-fg-3">
          Locked items are for {locked.ask ?? "an owner"} or another admin.
          They'll see them in their checklist.
        </p>
      ) : null}
    </div>
  );
};
